// src/pages/personal/analytics/FeedbackSection/components/RecommendationStats/RecommendationStats.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TEXT_STYLES, COLORS } from '@/theme';
import { RecommendChart } from './RecommendChart';
import { useResponsive } from '@/hooks/useResponsive';
import { Flex } from '@/components/ui/Layout';
import { formatNumber, formatPercent } from '@/utils/Formatters';
import styles from '../../FeedbackSection.module.css';

interface RecommendData {
  count: number;
  percentage: number;
}

interface RecommendationStatsProps {
  data: {
    yes: RecommendData;
    maybe: RecommendData;
    no: RecommendData;
  } | null;
  isLoading?: boolean;
}

const RecommendationStats: React.FC<RecommendationStatsProps> = ({ data, isLoading = false }) => {
  const { t, i18n } = useTranslation('analytics');
  const { isMobile } = useResponsive();
  
  // Цвета для категорий рекомендаций из темы
  const recommendColors = {
    yes: COLORS.status.success.dark,    // Зеленый - Да, однозначно
    maybe: COLORS.status.warning.dark,  // Желтый - Возможно
    no: COLORS.status.error.dark        // Красный - Нет
  };
  
  // Подготовка данных для графика
  const chartData = data ? [
    {
      name: t('feedback.recommendation.yes'),
      value: data.yes.count,
      percentage: data.yes.percentage,
      color: recommendColors.yes
    },
    {
      name: t('feedback.recommendation.maybe'),
      value: data.maybe.count,
      percentage: data.maybe.percentage,
      color: recommendColors.maybe
    },
    {
      name: t('feedback.recommendation.no'),
      value: data.no.count,
      percentage: data.no.percentage,
      color: recommendColors.no
    }
  ] : [];
  
  // Расчет рекомендательного индекса
  const recommendIndex = data 
    ? data.yes.percentage - data.no.percentage
    : 0;

  // Если нет данных, показываем сообщение
  if (!data && !isLoading) {
    return (
      <div>
        <div className="p-4 border-b bg-gray-50">
          <h3 className={TEXT_STYLES.h3}>{t('feedback.recommendation.title')}</h3>
        </div>
        <div className="p-4 h-[300px] flex items-center justify-center">
          <p className="text-gray-500">{t('no_data')}</p>
        </div>
      </div>
    );
  }
  
  // Состояние загрузки
  if (isLoading) {
    return (
      <div>
        <div className="p-4 border-b bg-gray-50">
          <h3 className={TEXT_STYLES.h3}>{t('feedback.recommendation.title')}</h3>
        </div>
        <div className="p-4 h-[300px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#245D33] mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('loading')}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      <div className="p-4 border-b bg-gray-50">
        <h3 className={TEXT_STYLES.h3}>{t('feedback.recommendation.title')}</h3>
      </div>
      <div className="p-4">
        {chartData.length > 0 ? (
          <Flex direction="column">
            <div className="mb-4 text-center">
              <div className="text-3xl font-bold">
                {formatNumber(recommendIndex, 1, true, i18n.language)}
              </div>
              <div className="text-sm text-gray-500">{t('feedback.recommendation.index')}</div>
            </div>
            {/* Используем унифицированный класс chartContainer */}
            <div className={styles.chartContainer}>
              <RecommendChart data={chartData} />
            </div>
            <div className="mt-4">
              <Flex 
                direction={isMobile ? "column" : "row"} 
                justify="center" 
                gap="md">
                {chartData.map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="font-bold" style={{ color: item.color }}>
                      {formatPercent(item.percentage, 1, true, i18n.language)}
                    </div>
                    <div>{item.name}</div>
                  </div>
                ))}
              </Flex>
            </div>
          </Flex>
        ) : (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-gray-500">{t('no_data')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationStats;